"use client";

import { Button } from "@watt/ui/components/ui/button";
import { useVerification } from "@watt/ui/components/verification/use-verification.ts";
import { VerificationActions } from "@watt/ui/components/verification/verification-actions";
import { VerificationCancel } from "@watt/ui/components/verification/verification-cancel";
import { VerificationError } from "@watt/ui/components/verification/verification-error";
import { VerificationField } from "@watt/ui/components/verification/verification-field";
import { VerificationInput } from "@watt/ui/components/verification/verification-input";
import { VerificationLabel } from "@watt/ui/components/verification/verification-label";
import { VerificationOTP } from "@watt/ui/components/verification/verification-otp";
import { VerificationOTPField } from "@watt/ui/components/verification/verification-otp-field";
import { VerificationResend } from "@watt/ui/components/verification/verification-resend";
import { VerificationStatus } from "@watt/ui/components/verification/verification-status";
import { VerificationTrigger } from "@watt/ui/components/verification/verification-trigger";
import { VerificationValue } from "@watt/ui/components/verification/verification-value";
import { Check as CheckIcon } from "lucide-react";
import type React from "react";
import { useImperativeHandle } from "react";

export interface EmailVerificationRef {
  reset: () => void;
}

interface EmailVerificationWrapperProps {
  onClearErrors: () => void;
  onChangeEmail?: () => Promise<void>;
  ref?: React.Ref<EmailVerificationRef>;
}

export function EmailVerificationWrapper({
  onChangeEmail,
  ref
}: EmailVerificationWrapperProps) {
  const { actions, state } = useVerification();

  useImperativeHandle(
    ref,
    () => ({
      reset: () => actions.reset()
    }),
    [actions]
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-center font-semibold text-2xl tracking-tight">
          {state.showOtp ? "Enter Verification Code" : "Forgot Password"}
        </h1>
        <p className="text-muted-foreground text-sm">
          {state.showOtp
            ? `We sent a 6-digit code to ${state.value || "your email"}`
            : "Enter your email to receive a verification code."}
        </p>
      </div>

      {/* Input state - shown when not verified and not showing OTP */}
      <VerificationField className="flex-col space-y-2">
        <VerificationLabel isRequired>Email</VerificationLabel>
        <VerificationInput
          className="h-12 w-full px-4 shadow-sm hover:bg-muted"
          id="email"
          placeholder="you@watt.com"
        />
        <VerificationError />
        <VerificationTrigger variant="default">Submit</VerificationTrigger>
      </VerificationField>

      {/* Verified state */}
      <VerificationStatus className="space-y-4">
        <div className="relative">
          <VerificationValue
            className="peer h-12 w-full px-4 pe-9 text-base text-muted-foreground shadow-sm"
            placeholder="Enter Email"
          />
          <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-green-600">
            <CheckIcon aria-hidden="true" size={16} />
          </div>
        </div>
        <Button
          className="h-auto p-0 text-sm"
          onClick={async () => {
            if (onChangeEmail) {
              await onChangeEmail();
            }
            actions.change();
          }}
          type="button"
          variant="link"
        >
          Change Email
        </Button>
      </VerificationStatus>

      {/* OTP input state */}
      <VerificationOTPField className="space-y-4">
        <VerificationOTP className="w-full" />
        <VerificationError />
        <VerificationActions className="flex-col space-y-3">
          <VerificationResend className="w-full p-3" variant="outline">
            {({ canResend, timeLeft }) =>
              canResend ? "Resend code" : `Resend code ${timeLeft}s`
            }
          </VerificationResend>
          <VerificationCancel className="w-full p-3" variant="ghost">
            Use different email
          </VerificationCancel>
        </VerificationActions>
      </VerificationOTPField>
    </div>
  );
}
